import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Users, Activity, Trash2, Ban, ShieldAlert, Eye, Edit } from 'lucide-react';
import { Input } from '@/components/ui/input';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userHistory, setUserHistory] = useState([]);
  const [editingUser, setEditingUser] = useState(null);
  const [editDays, setEditDays] = useState(0);
  const [editSubmitting, setEditSubmitting] = useState(false);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/all-users`);
      setUsers(response.data);
    } catch (err) {
      console.error('Error loading users:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadUserHistory = async (userId) => {
    try {
      const response = await axios.get(`${API}/admin/user-history/${userId}`);
      setUserHistory(response.data);
      setSelectedUser(userId);
    } catch (err) {
      console.error('Error loading history:', err);
    }
  };

  const handleUnsuspend = async (userId) => {
    if (!window.confirm('Réactiver ce compte ?')) return;
    
    try {
      await axios.post(`${API}/admin/unsuspend/${userId}`);
      alert('✅ Compte réactivé');
      loadUsers();
    } catch (err) {
      alert('Erreur lors de la réactivation');
    }
  };

  const handleSuspend = async (userId) => {
    if (!window.confirm('Suspendre ce compte ?')) return;
    
    try {
      await axios.post(`${API}/admin/suspend/${userId}`);
      alert('✅ Compte suspendu');
      loadUsers();
    } catch (err) {
      alert('Erreur lors de la suspension');
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('⚠️ ATTENTION : Supprimer définitivement ce compte ? Cette action est irréversible !')) return;
    
    try {
      await axios.delete(`${API}/admin/users/${userId}`);
      alert('✅ Compte supprimé');
      loadUsers();
    } catch (err) {
      alert('Erreur lors de la suppression');
    }
  };

  const handleOpenEdit = (user) => {
    setEditingUser(user);
    setEditDays(0);  // Toujours commencer à 0 pour "ajouter" des jours
  };

  const handleSaveEdit = async () => {
    if (!editingUser) return;
    
    try {
      setEditSubmitting(true);
      await axios.patch(`${API}/admin/users/${editingUser.id}/access`, {
        days: parseInt(editDays)
      });
      alert(`✅ Accès modifié : ${editDays} jours ajoutés`);
      setEditingUser(null);
      loadUsers();
    } catch (err) {
      alert('Erreur lors de la modification');
    } finally {
      setEditSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-white font-work">Chargement...</div>
      </div>
    );
  }

  const activeUsers = users.filter(u => u.has_access);
  const inactiveUsers = users.filter(u => !u.has_access);
  const suspendedUsers = users.filter(u => u.is_suspended);
  const warnedUsers = users.filter(u => u.sharing_warnings > 0 && !u.is_suspended);

  return (
    <div className="space-y-6">
      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-bg-secondary border-white/10">
          <CardContent className="p-4">
            <div className="text-center">
              <Users className="w-8 h-8 mx-auto mb-2 text-green-400" />
              <p className="text-2xl font-bebas text-white">{activeUsers.length}</p>
              <p className="text-xs text-text-secondary">Actifs</p>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-bg-secondary border-white/10">
          <CardContent className="p-4">
            <div className="text-center">
              <Users className="w-8 h-8 mx-auto mb-2 text-gray-400" />
              <p className="text-2xl font-bebas text-white">{inactiveUsers.length}</p>
              <p className="text-xs text-text-secondary">Inactifs</p>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-bg-secondary border-white/10">
          <CardContent className="p-4">
            <div className="text-center">
              <Ban className="w-8 h-8 mx-auto mb-2 text-red-400" />
              <p className="text-2xl font-bebas text-white">{suspendedUsers.length}</p>
              <p className="text-xs text-text-secondary">Suspendus</p>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-bg-secondary border-white/10">
          <CardContent className="p-4">
            <div className="text-center">
              <ShieldAlert className="w-8 h-8 mx-auto mb-2 text-yellow-400" />
              <p className="text-2xl font-bebas text-white">{warnedUsers.length}</p>
              <p className="text-xs text-text-secondary">Avertis</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Users List */}
      <Card className="bg-bg-secondary border-white/10">
        <CardHeader>
          <CardTitle className="text-white font-work">Liste des Comptes ({users.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left p-3 text-text-secondary font-work text-sm">Email</th>
                  <th className="text-left p-3 text-text-secondary font-work text-sm">Statut</th>
                  <th className="text-left p-3 text-text-secondary font-work text-sm">Expire</th>
                  <th className="text-left p-3 text-text-secondary font-work text-sm">Avertis.</th>
                  <th className="text-left p-3 text-text-secondary font-work text-sm">Vues</th>
                  <th className="text-left p-3 text-text-secondary font-work text-sm">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(user => (
                  <tr key={user.id} className="border-b border-white/5 hover:bg-white/5">
                    <td className="p-3">
                      <p className="text-white font-inter text-sm">{user.email}</p>
                      {user.is_trial && (
                        <span className="text-xs text-blue-400">Essai gratuit</span>
                      )}
                    </td>
                    <td className="p-3">
                      {user.is_suspended ? (
                        <span className="px-2 py-1 bg-red-900/30 text-red-200 text-xs rounded-full">
                          ⛔ Suspendu
                        </span>
                      ) : user.has_access ? (
                        <span className="px-2 py-1 bg-green-900/30 text-green-200 text-xs rounded-full">
                          ✅ Actif
                        </span>
                      ) : (
                        <span className="px-2 py-1 bg-gray-900/30 text-gray-200 text-xs rounded-full">
                          ⏸️ Inactif
                        </span>
                      )}
                    </td>
                    <td className="p-3 text-text-secondary text-sm">
                      {user.has_access ? `${user.days_remaining}j` : 'Expiré'}
                    </td>
                    <td className="p-3">
                      {user.sharing_warnings > 0 ? (
                        <span className="px-2 py-1 bg-yellow-900/30 text-yellow-200 text-xs rounded-full">
                          ⚠️ {user.sharing_warnings}/3
                        </span>
                      ) : (
                        <span className="text-text-secondary text-xs">-</span>
                      )}
                    </td>
                    <td className="p-3 text-text-secondary text-sm">
                      {user.watch_count}
                    </td>
                    <td className="p-3">
                      <div className="flex gap-2">
                        <Button
                          onClick={() => loadUserHistory(user.id)}
                          size="sm"
                          variant="outline"
                          className="border-white/20"
                          title="Voir historique"
                        >
                          <Eye className="w-3 h-3" />
                        </Button>
                        
                        <Button
                          onClick={() => handleOpenEdit(user)}
                          size="sm"
                          className="bg-blue-600 hover:bg-blue-700"
                          title="Modifier accès"
                        >
                          <Edit className="w-3 h-3" />
                        </Button>
                        
                        {user.is_suspended ? (
                          <Button
                            onClick={() => handleUnsuspend(user.id)}
                            size="sm"
                            className="bg-green-600 hover:bg-green-700"
                            title="Réactiver"
                          >
                            ✓
                          </Button>
                        ) : (
                          <Button
                            onClick={() => handleSuspend(user.id)}
                            size="sm"
                            className="bg-orange-600 hover:bg-orange-700"
                            title="Suspendre"
                          >
                            <Ban className="w-3 h-3" />
                          </Button>
                        )}
                        
                        <Button
                          onClick={() => handleDelete(user.id)}
                          size="sm"
                          variant="outline"
                          className="border-red-500/50 text-red-200 hover:bg-red-900/20"
                          title="Supprimer"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* User History Modal */}
      {selectedUser && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <Card className="bg-bg-secondary border-white/10 max-w-2xl w-full max-h-[80vh] overflow-hidden">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-white font-work">
                  <Activity className="w-5 h-5 inline mr-2" />
                  Historique de Visionnage
                </CardTitle>
                <Button onClick={() => setSelectedUser(null)} variant="ghost">
                  ✕
                </Button>
              </div>
            </CardHeader>
            <CardContent className="overflow-y-auto max-h-96">
              {userHistory.length === 0 ? (
                <p className="text-text-secondary text-center py-8">Aucun historique</p>
              ) : (
                <div className="space-y-2">
                  {userHistory.map((item, i) => (
                    <div key={i} className="p-3 bg-white/5 rounded-lg">
                      <p className="text-white font-work text-sm">{item.movie_title}</p>
                      <div className="flex gap-4 text-xs text-text-secondary mt-1">
                        <span>{new Date(item.watched_at).toLocaleString('fr-FR')}</span>
                        <span>IP: {item.ip}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Edit User Modal */}
      {editingUser && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <Card className="bg-bg-secondary border-white/10 max-w-md w-full">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-white font-work">
                  <Edit className="w-5 h-5 inline mr-2" />
                  Modifier l'Accès
                </CardTitle>
                <Button onClick={() => setEditingUser(null)} variant="ghost">
                  ✕
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-text-secondary text-sm mb-2">Email</p>
                <p className="text-white font-work">{editingUser.email}</p>
              </div>
              
              <div>
                <p className="text-text-secondary text-sm mb-2">Statut actuel</p>
                <p className="text-white">
                  {editingUser.has_access 
                    ? `✅ Actif (${editingUser.days_remaining} jours restants)`
                    : '❌ Expiré'
                  }
                </p>
              </div>
              
              <div>
                <label className="text-text-secondary text-sm mb-2 block">
                  Nombre de jours à ajouter
                </label>
                <Input
                  type="number"
                  value={editDays}
                  onChange={(e) => setEditDays(e.target.value)}
                  min="0"
                  max="36500"
                  className="bg-bg-tertiary border-white/20 text-white"
                  placeholder="Ex: 30 pour 1 mois"
                />
                <p className="text-xs text-text-secondary mt-2">
                  💡 Astuce : 30 jours = 1 mois, 365 = 1 an, 36500 = ~100 ans (permanent)
                </p>
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  onClick={() => setEditingUser(null)}
                  variant="outline"
                  className="flex-1"
                >
                  Annuler
                </Button>
                <Button
                  onClick={handleSaveEdit}
                  disabled={editSubmitting || !editDays}
                  className="flex-1 bg-netflix-red hover:bg-netflix-red-hover"
                >
                  {editSubmitting ? 'Modification...' : `Ajouter ${editDays}j`}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
